/**
 * Browser side of `.docx` import/export.
 *
 * Thin client over the server endpoints (`/api/documents/import|export/docx`,
 * `handlers::documents`) which run the `office-formats` Rust crate. Both sides
 * speak the **rich-text delta** (`DeltaOp[]`) — the same shape the CRDT core
 * (`LoroDocument.toDelta`) materialises — so an imported `.docx` drops straight
 * into a collaborative document and the merged result exports back.
 */
import type { DeltaOp } from "../collab/loro-document";

/** Upload a `.docx` file and get its content as a rich-text delta. */
export async function importDocx(
  file: Blob,
  apiFetch: (path: string, init: RequestInit) => Promise<Response> = (p, i) => fetch(p, i),
): Promise<DeltaOp[]> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const res = await apiFetch("/api/documents/import/docx", {
    method: "POST",
    headers: { "content-type": "application/octet-stream" },
    body: bytes,
  });
  if (!res.ok) {
    throw new Error(`docx import failed: ${res.status}`);
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  const body = (await res.json()) as { delta: DeltaOp[] };
  return body.delta;
}

/** Serialise a rich-text delta to a `.docx` and return it as a downloadable Blob. */
export async function exportDocx(
  delta: DeltaOp[],
  apiFetch: (path: string, init: RequestInit) => Promise<Response> = (p, i) => fetch(p, i),
): Promise<Blob> {
  const res = await apiFetch("/api/documents/export/docx", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ delta }),
  });
  if (!res.ok) {
    throw new Error(`docx export failed: ${res.status}`);
  }
  return await res.blob();
}
